import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button, Modal, useToast } from '../../components/common';
import PanelActions from '../../components/dev/PanelActions';
import api from '../../services/api';
import './ReadinessPanel.css';

/**
 * OS-Readiness — whether this app is fit to sit on the identity OS.
 *
 * Separate from Health on purpose. Health answers "is anyone using it, and is
 * it up?" — usage, latency, failures. Readiness answers "should it be here at
 * all?" and comes from three places that cost very different amounts:
 *
 *   Spec     the app record alone — free, evaluated live on every load
 *   Usage    a handful of indexed queries — cheap, also live
 *   Review   a model reading the CRUX — slow and billable, so cached and
 *            re-run only when asked
 *
 * The spec checks are evaluated live rather than read from the newest version
 * snapshot: snapshot_version() captures state *before* a change, so the latest
 * snapshot describes the spec from one save ago.
 */

/* Two families, because they are different kinds of claim and should not be
 * read as one list. Structure is measured — the record either says a thing or it
 * does not. Judgement is a model's opinion of prose, which is useful and is not
 * the same sort of fact. Structure comes first because it is what you can fix
 * without argument. */
const GROUPS = [
  { key: 'crux', family: 'structure', title: 'Design document', why: 'Whether the app explains itself.' },
  { key: 'dlr', family: 'structure', title: 'What it asks for', why: 'Whether the permissions are declared and resolvable.' },
  { key: 'auth', family: 'structure', title: 'How people get in', why: 'Whether authorization can complete.' },
  { key: 'webhook', family: 'structure', title: 'How it hears about change', why: 'Whether events arrive safely.' },
  { key: 'usage', family: 'structure', title: 'Who uses it', why: 'Whether anyone outside the people who built it has chosen it.' },
  { key: 'compounding', family: 'structure', title: 'What compounds', why: 'Whether what it writes is worth anything to the rest of the ecosystem.' },
  { key: 'llm', family: 'judgement', title: 'The CRUX, read closely', why: 'A model on what the document actually says. Runs itself when the CRUX changes.' },
];

const FAMILIES = [
  { key: 'structure', title: 'Structure', why: 'Measured from the app record and its usage. Facts.' },
  { key: 'judgement', title: 'Judgement', why: 'A model reading the design document. Opinion, and worth having.' },
];

// A mark rather than a sentence. The three differ by shape as well as colour —
// ✓, × and · are distinguishable without seeing green from red — and each
// carries a title so the meaning is available to a screen reader and on hover.
const STATE = {
  true: { mark: '✓', word: 'Met' },
  false: { mark: '×', word: 'Not met' },
  null: { mark: '·', word: 'Not applicable' },
};
/** Failing, then not-applicable, then passing. */
function rank(c) { return c.ok === false ? 0 : c.ok === null ? 1 : 2; }

/* Two of the three are derived from the app record, so they always have an
 * answer. Getting nothing back means this backend predates them — worth saying
 * out loud, because a dash reads as "nobody has judged it yet" and no amount of
 * pressing Run evaluations will change it. */
const STALE_BACKEND = 'Not returned by this backend — it may be running an older version';

/* How long to watch for a queued review, and how often. Long enough for a slow
 * read of a long document; short enough that a broker with no worker behind it
 * says so rather than spinning forever. */
const REVIEW_WAIT_MS = 180000;
const REVIEW_POLL_MS = 3000;

async function waitForReview(appId, previousRanAt) {
  const deadline = Date.now() + REVIEW_WAIT_MS;
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, REVIEW_POLL_MS));
    try {
      const state = await api.request(`/third-party/${appId}/readiness`);
      const ranAt = state?.review?.ran_at;
      if (ranAt && ranAt !== previousRanAt) return true;
    } catch {
      /* a blip mid-review is not a failure; keep watching */
    }
  }
  return false;
}

/**
 * One of the three headline scores.
 *
 * A level with no number is a real state, not a loading one: App-Value is a
 * judgement, and before anything has judged it the honest display is a dash.
 */
function ScoreCard({ title, asks, level, name, rungs, note, onInfo }) {
  const has = typeof level === 'number';
  return (
    <div className={`score ${has ? `score--l${level}` : 'score--none'}`}>
      <span className="score__title">
        {title}
        <button className="score__info" onClick={onInfo} aria-label={`What ${title} means`}>i</button>
      </span>
      <span className="score__value">
        <strong>{has ? level : '—'}</strong><em>/5</em>
      </span>
      <span className="score__name">{name || '—'}</span>
      <span className="score__rungs" aria-hidden="true">
        {(rungs || [0, 1, 2, 3, 4, 5].map(l => ({ level: l, reached: false }))).map(r => (
          <i key={r.level} className={r.reached ? `is-on score__rung-${r.level}` : ''} />
        ))}
      </span>
      <span className="score__asks">{note || asks}</span>
    </div>
  );
}

function ReadinessPanel({ appId, onGoToCrux }) {
  const toast = useToast();
  const [checks, setChecks] = useState(null);
  const [version, setVersion] = useState(null);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rerunning, setRerunning] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState(null);
  const [ranAt, setRanAt] = useState(null);
  const [ladder, setLadder] = useState(null);
  const [rubric, setRubric] = useState(null);
  const [showRubric, setShowRubric] = useState(false);
  const [value, setValue] = useState(null);
  const [usage, setUsage] = useState(null);
  const [cruxEvidence, setCruxEvidence] = useState(null);
  const [blocked, setBlocked] = useState(null);
  // null, or which of the three ladders is open — one modal, three ladders.
  const [showLevels, setShowLevels] = useState(null);
  const [levelsTab, setLevelsTab] = useState('rubric');
  const [sort, setSort] = useState('status');   // 'status' | 'name'
  const [filter, setFilter] = useState('all');  // 'all' | 'bad' | 'ok'

  const load = useCallback(async ({ rerun } = {}) => {
    if (rerun) setRerunning(true); else setLoading(true);
    setError(null);
    try {
      const res = await api.request(`/third-party/${appId}/readiness`);
      setChecks(res?.checks || []);
      setVersion(res?.version ?? null);
      setReview(res?.review || null);
      setLadder(res?.ladder || null);
      setValue(res?.value || null);
      setUsage(res?.usage || null);
      setCruxEvidence(res?.crux_evidence || null);
      setBlocked(res?.blocked || null);
      setRanAt(new Date());
    } catch (err) {
      setError(err.message || 'Could not load readiness.');
    }
    setLoading(false); setRerunning(false);
  }, [appId]);

  useEffect(() => { load(); }, [load]);

  // Fetched on demand: most visits are about this app's result, not the standard
  // it was held to.
  const openRubric = async () => {
    setShowRubric(v => !v);
    if (rubric) return;
    try {
      setRubric(await api.request('/third-party/readiness/rubric'));
    } catch {
      setRubric({ markdown: 'The rubric could not be loaded.' });
    }
  };

  /**
   * One action, because two buttons implied two things a developer should
   * reason about. They should not: the structural checks are free and the
   * review is not, so the sequence is always "re-run everything, and re-read
   * the document too if a model is available".
   */
  const runAll = async () => {
    setReviewing(true);
    try {
      if (review?.available) {
        // The review is queued, not awaited — a model reading a whole document
        // takes longer than the edge holds a connection open. Poll until the
        // stored ran_at moves, which is the only signal the work landed.
        const before = review?.ran_at || null;
        const res = await api.request(`/third-party/${appId}/readiness/review`, { method: 'POST' });
        if (res?.status === 'queued') {
          const landed = await waitForReview(appId, res.previous_ran_at ?? before);
          if (!landed) {
            await load({ rerun: true });
            toast.info('Still reading the CRUX. It will appear here when it lands.');
            setReviewing(false);
            return;
          }
        }
      }
      await load({ rerun: true });
      toast.success('Evaluations complete.');
    } catch (err) {
      toast.error(err.message || 'The evaluations could not finish.');
    }
    setReviewing(false);
  };

  const families = useMemo(() => {
    if (!checks) return [];
    const by = {};
    for (const c of checks) {
      const key = String(c.id || '').split('.')[0] || 'other';
      (by[key] = by[key] || []).push(c);
    }
    const order = GROUPS.map(g => g.key);
    const keep = c => filter === 'all'
      || (filter === 'bad' && c.ok === false)
      || (filter === 'ok' && c.ok === true);

    const groups = Object.entries(by)
      .map(([key, items]) => ({
        ...(GROUPS.find(g => g.key === key) || { key, family: 'structure', title: key, why: '' }),
        items: [...items].filter(keep).sort((a, b) =>
          sort === 'name'
            ? a.label.localeCompare(b.label)
            : rank(a) - rank(b) || a.label.localeCompare(b.label)),
        passed: items.filter(c => c.ok === true).length,
        scored: items.filter(c => c.ok !== null).length,
      }))
      .sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));

    return FAMILIES.map(f => {
      const mine = groups.filter(g => g.family === f.key);
      return {
        ...f,
        groups: mine,
        passed: mine.reduce((n, g) => n + g.passed, 0),
        scored: mine.reduce((n, g) => n + g.scored, 0),
        shown: mine.reduce((n, g) => n + g.items.length, 0),
      };
    }).filter(f => f.groups.length);
  }, [checks, sort, filter]);

  if (loading) return <p className="ready__msg">Loading…</p>;
  if (error) return <p className="ready__msg ready__msg--bad">{error}</p>;

  // Nothing to score, and nothing that pressing a button would change. Three
  // hedging numbers about a document that does not exist read as a result.
  if (blocked) {
    return (
      <div className="ready ready--blocked">
        <h3>No evaluations yet</h3>
        <p>{blocked.message}</p>
        {onGoToCrux && (
          <Button onClick={onGoToCrux}>Write the CRUX</Button>
        )}
      </div>
    );
  }
  if (!checks?.length) return <p className="ready__msg">No checks ran.</p>;

  const scored = checks.filter(c => c.ok !== null);
  const passed = scored.filter(c => c.ok).length;
  const pct = scored.length ? Math.round((passed / scored.length) * 100) : 0;
  const notRun = checks.filter(c => c.ok === null).length;

  const failing = scored.length - passed;
  const LADDERS = {
    value: {
      title: 'Societal value add — the claim',
      intro: 'If this app were finished, how much would it change the people using it — and the '
        + 'world, if everyone used it? Compounding is judged here: what gets better with continued '
        + 'use, and what other apps gain from what this one writes. Nothing on this ladder is '
        + 'measurable, so it comes from the model reading your CRUX, judged against what the '
        + 'document actually says.',
      rungs: value?.rungs,
      level: value?.assessed ? value.level : null,
      name: value?.assessed ? value.name : 'Not assessed',
      definition: value?.assessed ? value.definition : null,
      why: value?.assessed ? value.why : null,
      foot: value?.assessed
        ? 'A reading of the claim, not of the app. It moves when the claim you make moves — and '
          + 'most apps sit at 1 or 2, which is more useful to know than flattery.'
        : 'Not assessed yet — run the evaluations. Until then this is blank rather than zero, '
          + 'because nobody has judged it.',
    },
    readiness: {
      title: 'Readiness — how far toward the claim',
      intro: 'How far along the app is toward delivering that value in real lives and real '
        + 'workflows. Not how much was built — whether a person can complete a workflow. Each rung '
        + 'needs every rung below it, so the level is a floor: it can be behind what you have '
        + 'actually built, never ahead of it.',
      rungs: ladder?.rungs,
      level: ladder?.level ?? null,
      name: ladder?.name,
      definition: ladder?.definition,
      foot: 'A tick is something measured here. A dot is something only you can confirm — the level '
        + 'is what the evidence supports, so it is a floor rather than a verdict.',
    },
    usage: {
      title: 'Paradigm feature usage — what it stands on',
      intro: 'Tiers of participation, not a count of features — a checklist would reward an app '
        + 'that requests everything. The rungs go registered, identity, embedded, multiplayer, '
        + 'acting, autonomous, and each one assumes every rung under it.',
      rungs: usage?.rungs,
      level: usage?.level ?? null,
      name: usage?.name,
      definition: usage?.definition,
      foot: usage?.unverified
        ? `${usage.unverified} surfaces leave no trace the platform can read — the shell, identity `
          + 'switching, Personas, your own database. Declare them in the CRUX feature table and '
          + 'they stop showing as unknown.'
        : 'A surface an app has no reason to touch never counts against it. Level 0 is a question '
          + 'rather than an insult: if ofSelf is only supplying the login, what is it doing here?',
    },
  };
  const openLadder = showLevels ? LADDERS[showLevels] : null;

  const shown = families.reduce((n, f) => n + f.shown, 0);

  return (
    <div className="ready">
      <PanelActions>
        <span className="ready__run-note">
          Version {version}
          {ranAt && <> · last run {ranAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>}
        </span>
        <Button onClick={runAll} disabled={reviewing || rerunning}>
          {reviewing || rerunning ? 'Running…' : 'Run evaluations'}
        </Button>
      </PanelActions>

      {/* Three questions, asked separately because one number for an app is a
          lie by averaging — and the interesting states are the disagreements. A
          high claim with low readiness is worth finishing; high readiness
          against a low claim is a well-made app nobody needed. */}
      <div className="ready__scores">
        <ScoreCard
          title="Societal value add"
          asks="How much would it change people, and the world?"
          level={value?.assessed ? value.level : null}
          name={value?.assessed ? value.name : 'Not assessed'}
          rungs={value?.rungs}
          // A judgement nobody has made yet is blank, not zero. Scoring an
          // unreviewed app 0 would be a claim the platform has not earned.
          note={value?.assessed ? null : 'Run the evaluations to have it read'}
          onInfo={() => { setLevelsTab('rubric'); setShowLevels('value'); }}
        />
        <ScoreCard
          title="Readiness"
          asks="How far toward delivering that claim?"
          level={ladder?.level}
          name={ladder?.name}
          rungs={ladder?.rungs}
          note={ladder ? (ladder.next && `Next: ${ladder.next.name}`) : STALE_BACKEND}
          onInfo={() => { setLevelsTab('rubric'); setShowLevels('readiness'); }}
        />
        <ScoreCard
          title="Paradigm feature usage"
          asks="How much of the OS is it standing on?"
          level={usage?.level}
          name={usage?.name}
          rungs={usage?.rungs}
          note={usage ? `Using ${usage.using} of ${usage.of} surfaces` : STALE_BACKEND}
          onInfo={() => { setLevelsTab('rubric'); setShowLevels('usage'); }}
        />
      </div>
      <p className="ready__why">
        Three questions, asked separately: one number for an app would be a lie by averaging,
        and the interesting states are where they disagree.
      </p>

      <div className="ready__controls">
        <p className="ready__tally">
          <span><strong>{passed}</strong> of {scored.length} checks met</span>
          <span className={failing ? 'is-bad' : ''}><strong>{failing}</strong> to fix</span>
          <span><strong>{notRun}</strong> not applicable</span>
        </p>
        <div className="ready__filter" role="group" aria-label="Filter checks">
          {[['all', `All ${checks.length}`], ['bad', `Problems ${failing}`], ['ok', `Met ${passed}`]]
            .map(([k, label]) => (
              <button key={k} className={filter === k ? 'is-active' : ''} onClick={() => setFilter(k)}>
                {label}
              </button>
            ))}
        </div>
        <button className="ready__rubric-link" onClick={openRubric}>What the review asks →</button>
      </div>

      <table className="ready__table">
        <thead>
          <tr>
            <th className="ready__th-state">
              <button onClick={() => setSort('status')} className={sort === 'status' ? 'is-active' : ''}>Result</button>
            </th>
            <th>
              <button onClick={() => setSort('name')} className={sort === 'name' ? 'is-active' : ''}>Test</button>
            </th>
            <th>What it found</th>
          </tr>
        </thead>
        <tbody>
          {shown === 0 && (
            <tr><td colSpan={3} className="ready__detail">Nothing matches that filter.</td></tr>
          )}
          {/* Structure, then judgement, each said once as a heading rather than
              tagged on every row; the area each check belongs to sits under it. */}
          {families.filter(f => f.shown).map(f => (
            <React.Fragment key={f.key}>
              <tr className="ready__fam">
                <td colSpan={3}><b>{f.title}</b> <span>{f.why}</span></td>
              </tr>
              {f.groups.filter(g => g.items.length).map(g => (
                <React.Fragment key={g.key}>
                  <tr className="ready__grp"><td colSpan={3}>{g.title}</td></tr>
                  {g.items.map(c => (
                    <tr key={c.id} className={c.ok === null ? 'is-na' : c.ok ? 'is-ok' : 'is-bad'}>
                      <td className="ready__state-cell">
                        <span className="ready__state" title={STATE[String(c.ok)].word}>
                          <span aria-hidden="true">{STATE[String(c.ok)].mark}</span>
                          <span className="sr-only">{STATE[String(c.ok)].word}</span>
                        </span>
                      </td>
                      <td className="ready__label">{c.label}</td>
                      <td className="ready__detail">{c.detail || '—'}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {showLevels && openLadder && (
        <Modal isOpen onClose={() => setShowLevels(null)} title={openLadder.title} size="md">
          {/* Two tabs, because these answer different questions with the same
              rungs. The rubric is the standard — identical for every app, and
              readable before you have one. The report card is this app against
              it. Interleaving them made the standard look like a verdict. */}
          <div className="levels__tabs" role="tablist" aria-label="What this score means">
            {[['rubric', 'Rubric'], ['report', 'Report card']].map(([k, label]) => (
              <button
                key={k}
                role="tab"
                aria-selected={levelsTab === k}
                className={levelsTab === k ? 'is-active' : ''}
                onClick={() => setLevelsTab(k)}
              >
                {label}
              </button>
            ))}
          </div>

          {levelsTab === 'rubric' ? (
            <>
              <p className="levels__intro">{openLadder.intro}</p>
              <ol className="levels levels--plain">
                {(openLadder.rungs || []).map(r => (
                  <li key={r.level} className={`levels__row levels__row--${r.level}`}>
                    <span className="levels__n">{r.level}</span>
                    <div className="levels__body">
                      <div className="levels__name"><span>{r.name}</span></div>
                      <p className="levels__def">{r.definition}</p>
                      {r.evidence && <p className="levels__ev">{r.evidence}</p>}
                    </div>
                  </li>
                ))}
              </ol>
              {showLevels === 'value' && (
                <p className="levels__foot">
                  Judged by a model reading your CRUX, against this ladder and nothing else.
                  <button className="levels__link" onClick={() => { setShowLevels(null); openRubric(); }}>
                    The rest of what the review asks →
                  </button>
                </p>
              )}
            </>
          ) : (
            <>
              <p className="levels__verdict">
                <strong className={openLadder.level === null ? 'is-none' : `is-l${openLadder.level}`}>
                  {openLadder.level === null ? '—' : openLadder.level}<em>/5</em>
                </strong>
                <span>
                  <b>{openLadder.name || 'Not assessed'}</b>
                  {openLadder.definition && <i>{openLadder.definition}</i>}
                </span>
              </p>
              {openLadder.why && <p className="levels__why">“{openLadder.why}”</p>}

              <ol className="levels">
                {(openLadder.rungs || []).map(r => (
                  <li key={r.level} className={`levels__row levels__row--${r.level} ${r.reached ? 'is-reached' : ''}`}>
                    <span className="levels__n">{r.level}</span>
                    <div className="levels__body">
                      <div className="levels__name">
                        <span>{r.name}</span>
                        <em className={r.reached ? 'is-reached' : ''}>
                          {r.reached ? 'Reached' : 'Not yet'}
                        </em>
                      </div>

                      {/* Why this rung was or was not awarded, where the rung is
                          rather than on the page behind it. */}
                      {(r.requirements || []).length > 0 && (
                        <ul className="levels__reqs">
                          {r.requirements.map(q => (
                            <li key={q.id} className={q.ok ? 'is-ok' : 'is-bad'}>
                              <span aria-hidden="true">{q.ok ? '✓' : '✕'}</span> {q.label}
                            </li>
                          ))}
                        </ul>
                      )}
                      {(r.unverifiable || []).length > 0 && (
                        <ul className="levels__reqs levels__reqs--human">
                          {r.unverifiable.map(u => (
                            <li key={u}><span aria-hidden="true">·</span> {u}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </li>
                ))}
              </ol>

              {/* Paradigm feature usage lists the surfaces themselves: the rungs
                  say what kind of participation each is, this says which are on. */}
              {showLevels === 'usage' && usage?.features && (
                <>
                  <h4 className="levels__subhead">Every surface, and whether this app uses it</h4>
                  <ul className="levels__features">
                    {usage.features.map(f => (
                      <li key={f.id} className={f.used === null ? 'is-na' : f.used ? 'is-ok' : 'is-off'}>
                        <span aria-hidden="true">{f.used === null ? '?' : f.used ? '✓' : '·'}</span>
                        {f.label}
                        {f.declared_only && <em>from the CRUX</em>}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* The CRUX is written to answer these three questions, so a
                  score can say which parts of the document it is resting on —
                  and which of them are still empty, which is a score with
                  nothing under it rather than a neutral omission. */}
              {(cruxEvidence?.[showLevels] || []).length > 0 && (
                <>
                  <h4 className="levels__subhead">
                    What the CRUX says
                    <span>
                      {cruxEvidence[showLevels].filter(x => x.written).length}
                      {' of '}
                      {cruxEvidence[showLevels].length} written
                    </span>
                  </h4>
                  <ul className="levels__evidence">
                    {cruxEvidence[showLevels].map(x => (
                      <li key={x.label} className={x.written ? 'is-ok' : 'is-missing'}>
                        <span aria-hidden="true">{x.written ? '✓' : '✕'}</span>
                        {x.label}
                        {!x.required && <em>optional</em>}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* What would move it up, in the ladder's own words. The rung list
                  says where it stands; without this the answer to "so what do I
                  do" is on a different tab. */}
              {(() => {
                const next = (openLadder.rungs || []).find(r => r.level === (openLadder.level ?? -1) + 1);
                return next && (
                  <p className="levels__next">
                    <b>To reach {next.level} · {next.name}</b>
                    {next.definition}
                  </p>
                );
              })()}

              <p className="levels__foot">{openLadder.foot}</p>
            </>
          )}
        </Modal>
      )}

      {showRubric && (
        <Modal isOpen onClose={() => setShowRubric(false)} title="The review rubric" size="lg">
          <div className="ready__rubric-body">
            {rubric
              ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{rubric.markdown}</ReactMarkdown>
              : <p className="ready__msg">Loading…</p>}
          </div>
        </Modal>
      )}

    </div>
  );
}

/* How the scores have moved lives on the Versions tab, not here.
 *
 * This tab answers "how is it now"; a score is only readable against the
 * version that produced it, and that history — every version, who cut it, what
 * the change did — already exists over there. Both panels fetched
 * /readiness/history and only one of them rendered it. */

export default ReadinessPanel;
